import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { lighten } from '@material-ui/core/styles/colorManipulator';

import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';

import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import Pagination from '@material-ui/lab/Pagination';

import TicketIcon from '@material-ui/icons/ConfirmationNumber';
import UserIcon from '@material-ui/icons/Person';

import InviteGuestDialog from 'app/components/event/InviteGuestDialog';
import EditInvitationDialog from 'app/components/event/EditInvitationDialog';
import EditDateDialog from 'app/components/event/EditDateDialog';
import EditDatesDialog from 'app/components/event/EditDatesDialog';
import SelectTicketDialog from 'app/components/event/SelectTicketDialog';
import PurchaseTicketDialog from 'app/components/accounting/payments/PurchaseTicketDialog';
import PageLoading from 'app/components/pages/PageLoading';
import Loading from 'app/components/loaders/Loading';

import GoogleMapDefaultStyles from 'app/utils/GoogleMapDefaultStyles';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative'
    },
    imageContainerFull: {
        position: 'relative',
        paddingBottom: '30%'
    },
    imageContainerMobile: {
        position: 'relative',
        paddingBottom: '40%'
    },
    imageContainerPhone: {
        position: 'relative',
        paddingBottom: '55%',
        minHeight: 300
    },
    imageDiv: {
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        position: 'absolute',
        zIndex: '1097',
        backgroundPosition: 'center',
        backgroundSize: 'cover'
    },
    textDiv: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        display: "grid",
        color: "white",
        alignItems: "center",
        textAlign: 'center',
        alignContent: 'space-evenly',
        justifyContent: 'center',
        zIndex: '1099'
    },
    textItem: {
        paddingLeft: 20,
        paddingRight: 20,
        maxWidth: 600,
    },
    overlay: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        backgroundColor: '#000000',
        opacity: '40%',
        zIndex: '1098'
    },
    centeredText: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: 'center',
        padding: theme.spacing(10),
        marginLeft: '20%',
        marginRight: '20%'
    },
    textCenter: {
        textAlign: 'center'
    },
    description: {
        maxWidth: 680,
        margin: `${theme.spacing(6)}px auto`,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    textMobile: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        padding: theme.spacing(6),
        marginLeft: '10%',
        marginRight: '10%'
    },
    actionsBanner: {
        backgroundColor: theme.palette.secondary.dark,
        paddingTop: theme.spacing(10),
        paddingBottom: theme.spacing(10),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        color: 'white',
        position: 'relative'
    },
    googleMap: {
        border: '0',
        width: '100%',
        height: '500px'
    },
    invitationContainer: {
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    guestPaper: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(3)
    },
    infoWindow: {
        padding: theme.spacing(2),
        overflowY: 'none',
        overflowX: 'none'
    },
    outlinedButton: {
        borderColor: '#fff',
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.22);'
        },
        '&.Mui-disabled': {
            border: '1px solid rgba(255, 255, 255, 0.52)',
            color: 'rgba(255, 255, 255, 0.52)'
        }
    },
    clubAvatar: {
        backgroundColor: theme.palette.secondary.main
    },
    buttonGroup: {
        '& .MuiButtonGroup-grouped': {
            borderColor: '#fff',
            color: '#fff',
        },
        '& .MuiButton-contained': {
            backgroundColor: '#fff',
            color: `${theme.palette.secondary.main} !important`,
        }
    },
    chipSuccess: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff',
    },
    chipWarning: {
        backgroundColor: theme.palette.warning.dark,
        color: '#ffffff',
    },
    chipError: {
        backgroundColor: theme.palette.error.main,
        color: '#ffffff',
    },
    card: {
        justifyContent: 'space-between'
    },
    cardAction: {
        alignSelf: 'auto'
    },
    bottomRadius: {
        borderRadius: theme.shape.borderRadius
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    code: {
        maxWidth: 200,
        width: '100%',
        height: 'auto',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0
    }
}))

const rgbStringToHex = (rgb) => {
    return rgb.substring(4, rgb.length-1)
         .replace(/ /g, '')
         .split(',');
}

const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => {
    const hex = parseInt(x).toString(16)
    return hex.length === 1 ? '0' + hex : hex
}).join('');

const setMapStyles = (feature, color, styles, lightenAmount = 0.75) => {
    const i = styles.findIndex(s => s?.featureType === feature);

    let lightenedColor = lighten(color, lightenAmount);

    if (lightenedColor.charAt(0) !== '#') {
        lightenedColor = rgbStringToHex(lightenedColor);
        lightenedColor = rgbToHex(lightenedColor[0], lightenedColor[1], lightenedColor[2]);
    }

    if (i > -1) styles[i].stylers[0].color = lightenedColor;

    return styles;
}

function getProcessingInvoice(invitation) {
    return invitation?.invoice?.payments?.find((p) => p.status === 'processing');
}

const mapStyles = {
    border: '0',
    width: '100%',
    height: '400px'
}

export default function ViewEvent(props) {

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const params = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isPhone = useMediaQuery(theme.breakpoints.down('xs'));

    const [ invitation, setInvitation ] = useState(null);

    const [ loading, setLoading ] = useState(true);
    const [ bannerLoading, setBannerLoading ] = useState(false);

    const [ activeInvitation, setActiveInvitation ] = useState(null);
    const [ activeDate, setActiveDate ] = useState(null);
    const [ activeInvoiceInvitation, setActiveInvoiceInvitation ] = useState(null);

    const [ editGuestDialogOpen, setEditGuestDialogOpen ] = useState(false);
    const [ inviteGuestDialogOpen, setInviteGuestDialogOpen ] = useState(false);
    const [ editDateDialogOpen, setEditDateDialogOpen ] = useState(false);
    const [ editDatesDialogOpen, setEditDatesDialogOpen ] = useState(false);
    const [ purchaseTicketDialogOpen, setPurchaseTicketDialogOpen ] = useState(false);
    const [ selectTicketDialogOpen, setSelectTicketDialogOpen ] = useState(false);

    const [ guestsPage, setGuestsPage ] = useState(1);
    const [ datesPage, setDatesPage ] = useState(1);
    const [ filteredGuests, setFilteredGuests ] = useState([]);
    const [ guestSearchValue, setGuestSearchValue ] = useState("");
    const [ filteredDates, setFilteredDates ] = useState([]);
    const [ dateSearchValue, setDateSearchValue ] = useState("");

    const [ location, setLocation ] = useState(null);
    const [ map, setMap ] = useState(null);
    const [ marker, setMarker ] = useState(null);
    const [ infoWindow, setInfoWindow ] = useState(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: "AIzaSyDN1_PZDAq2mCnTFGc279pXo-MIIdqugME",
        libs: ['places']
    });

    // Getter Functions ---------------------------------------------------------------------

    const getInvitation = () => {
        setLoading(true);

        api.get(`/invitation/${params.id}`)
            .then(res => setInvitation(res.invitation))
            .finally(() => setLoading(false))
    }

    const getInvitationBanner = () => {

        api.get(`/invitation/${params.id}`)
            .then(res => setInvitation(res.invitation))
            .finally(() => setBannerLoading(false))
    }

    useEffect(() => {
        getInvitation();
    }, []);

    useEffect(() => {
        if (invitation) {
            setFilteredGuests(invitation.guest.invitations_sent);
            setGuestSearchValue("");
            setGuestsPage(1);

            setFilteredDates(invitation.dates);
            setDateSearchValue("");
            setDatesPage(1);
        }
    }, [invitation])

    useEffect(() => {
        if (isLoaded && invitation && invitation.event.location != "" && invitation.event.formatted_address.replace(/[, ]+/g,' ').trim() != "") {

            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ address: invitation.event.formatted_address }).then(({ results }) => {
                if (results[0].geometry && results[0].geometry.location) setLocation(results[0]);
            });
        }
    }, [invitation, isLoaded]);

    // Page functions ---------------------------------------------------------------------

    const handleChangeGuestsPage = (event, newPage) => {
        setGuestsPage(newPage)
    };

    const handleChangeDatesPage = (event, newPage) => {
        setDatesPage(newPage)
    };

    const handleGuestSearchValue = event => {
        event.preventDefault();

        const newSearchValue = event.target.value

        let newFilteredGuests = invitation.guest.invitations_sent.filter((d) => {
            let match = false;
            if (d.full_name !== null) {
                let m = d.guest.full_name.toLowerCase().includes(newSearchValue.toLowerCase());
                if (m) match = true;
            }
            return match;
        })

        setFilteredGuests(newFilteredGuests);
        setGuestSearchValue(newSearchValue);
        setGuestsPage(1);
    };

    const handleDateSearchValue = event => {
        event.preventDefault();

        const newSearchValue = event.target.value

        let newFilteredDates = invitation.dates.filter((d) => {
            let match = false;
            if (d.full_name !== null) {
                let m = d.name.toLowerCase().includes(newSearchValue.toLowerCase());
                if (m) match = true;
            }
            return match;
        })

        setFilteredDates(newFilteredDates);
        setDateSearchValue(newSearchValue);
        setDatesPage(1);
    };

    // Poster Functions ---------------------------------------------------------------------

    const postAccept = () => {
        setBannerLoading(true);
        api.get(`/invitation/${invitation.id}/accept`).then(getInvitationBanner);
    }

    const postMaybe = () => {
        setBannerLoading(true);
        api.get(`/invitation/${invitation.id}/maybe`).then(getInvitationBanner);
    }

    const postDecline = () => {
        setBannerLoading(true);
        api.get(`/invitation/${invitation.id}/decline`).then(getInvitationBanner);
    }

    const postResend = (id) => {
        setBannerLoading(true);
        api.post(`/invitation/${id}/resend`).then(getInvitationBanner)
    }

    // Invite Guest Dialog Functions ------------------------------------------

    const openInviteGuestDialog = () => {
        setInviteGuestDialogOpen(true)
    }

    const closeInviteGuestDialog = () => {
        setInviteGuestDialogOpen(false);
    }

    const closeInviteGuestDialogAndRefresh = () => {
        closeInviteGuestDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    // Select Ticket Dialog Functions ------------------------------------------

    const openSelectTicketDialog = () => {
        setSelectTicketDialogOpen(true)
    }

    const closeSelectTicketDialog = () => {
        setSelectTicketDialogOpen(false);
    }

    const closeSelectTicketDialogAndRefresh = () => {
        closeSelectTicketDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    // Edit Guest Dialog Functions ------------------------------------------

    const openEditGuestDialog = i => {
        setActiveInvitation(i);
        setEditGuestDialogOpen(true)
    }

    const closeEditGuestDialog = () => {
        setEditGuestDialogOpen(false);
        setActiveInvitation(null);
    }

    const closeEditGuestDialogAndRefresh = () => {
        closeEditGuestDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    const editGuestDialogClosed = () => {
        setActiveInvitation(null);
    }

    // Edit Guest Dates Dialog Functions ------------------------------------------

    const openEditDatesDialog = i => {
        setActiveInvitation(i);
        setEditDatesDialogOpen(true)
    }

    const closeEditDatesDialog = () => {
        setEditDatesDialogOpen(false);
    }

    const closeEditDatesDialogAndRefresh = () => {
        closeEditDatesDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    const editDatesDialogClosed = () => {
        setActiveInvitation(null);
    }

    // Invite Date Dialog Functions ------------------------------------------

    const openEditDateDialog = d => {
        setActiveDate(d);
        setEditDateDialogOpen(true)
    }

    const closeEditDateDialog = () => {
        setEditDateDialogOpen(false);
    }

    const closeEditDateDialogAndRefresh = () => {
        closeEditDateDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    const editDateDialogClosed = () => {
        setActiveDate(null);
    }

    // Charge Card Dialog --------------------------------------

    const openPurchaseTicketDialog = i => {
        setActiveInvoiceInvitation(i);
        setPurchaseTicketDialogOpen(true);
    }

    const closePurchaseTicketDialog = () => {
        setPurchaseTicketDialogOpen(false);
    }

    const closePurchaseTicketDialogAndRefresh = () => {
        closePurchaseTicketDialog();
        setBannerLoading(true);
        getInvitationBanner();
    }

    const purchaseTicketDialogClosed = () => {
        setActiveInvoiceInvitation(null);
    }

    // Map Functions

    const onMapLoad = useCallback(
        function onLoad (map) {

            let mapStyles = GoogleMapDefaultStyles;

            mapStyles = setMapStyles('road.arterial', theme.palette.secondary.main, mapStyles, 0.70);
            mapStyles = setMapStyles('road.highway', theme.palette.secondary.main, mapStyles, 0.55);

            map.setOptions({ styles: mapStyles});
            map.setZoom(14);
            map.setCenter(location.geometry.location);

            setMap(map);
        }
    )

    const onMarkerLoad = useCallback(
        function onLoad (marker) {

            setMarker(marker);
        }
    )

    const onInfoWindowLoad = useCallback(
        function onLoad (iw) {
            setInfoWindow(iw);
        }
    )

    const renderMap = () => {

        const locIcon = encodeURIComponent(
            `<svg version="1.1" id="Warstwa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 430 430" style="enable-background:new 0 0 430 430;" xml:space="preserve">\
                <style type="text/css">\
                    .st0{fill:#121331;}\
                    .st1{fill:${theme.palette.secondary.light};}\
                    .st2{fill-opacity:0;}\
                    .st3{fill:none;}\
                </style>\
                <g>\
                    <g>\
                        <path class="st2" d="M267.73,302.95c39.45,6.31,66.59,19.48,66.59,34.7l0,0c0,21.36-53.42,38.67-119.32,38.67\
                            S95.68,359.01,95.68,337.65c0-15.22,27.14-28.39,66.59-34.7"/>\
                        <path class="st1" d="M215,382.55c-32.51,0-63.15-4.13-86.29-11.63c-25.68-8.32-39.26-19.83-39.26-33.27c0-6.87,3.8-17,21.9-26.24\
                            c12.35-6.31,29.62-11.36,49.94-14.61c3.4-0.54,6.6,1.77,7.14,5.17c0.54,3.4-1.77,6.6-5.17,7.14\
                            c-40.27,6.44-61.34,19.24-61.34,28.55c0,13.54,43.02,32.44,113.08,32.44s113.08-18.89,113.08-32.44c0-9.31-21.07-22.1-61.34-28.55\
                            c-3.4-0.54-5.71-3.74-5.17-7.14c0.54-3.4,3.74-5.71,7.14-5.17c20.31,3.25,37.58,8.3,49.94,14.61c18.1,9.24,21.9,19.37,21.9,26.24\
                            c0,13.44-13.58,24.95-39.26,33.27C278.15,378.42,247.51,382.55,215,382.55z"/>\
                    </g>\
                    <g>\
                        <path class="st2" d="M132.92,195.57c-3.13-5.46-5.91-11.14-7.91-17.11c-3.15-9.43-4.85-19.53-4.85-30.02\
                            c0-54.41,45.86-98.11,101.06-94.56c46.8,3.01,84.86,40.78,88.23,87.56c0.88,12.21-0.57,23.97-3.93,34.89\
                            c-2.09,6.79-5.13,13.25-8.66,19.41l-81.92,142.68L132.92,195.57z"/>\
                        <path class="st0" d="M214.94,344.67c-2.23,0-4.29-1.19-5.41-3.13l-82.02-142.86c0,0,0,0,0,0c-3.74-6.52-6.49-12.49-8.42-18.24\
                            c-3.43-10.28-5.17-21.04-5.17-32c0-27.8,11.64-54.65,31.92-73.67c20.58-19.3,47.49-28.92,75.78-27.1\
                            c50.01,3.22,90.44,43.35,94.04,93.34c0.91,12.67-0.5,25.18-4.19,37.17c-2.06,6.7-5.07,13.46-9.21,20.68l-81.92,142.69\
                            C219.23,343.47,217.17,344.67,214.94,344.67z M138.32,192.47l76.62,133.45l76.51-133.27c3.67-6.4,6.33-12.34,8.11-18.14\
                            c3.23-10.51,4.47-21.49,3.67-32.61c-3.16-43.81-38.58-78.97-82.41-81.79c-24.81-1.59-48.41,6.84-66.45,23.76\
                            c-18.04,16.92-27.98,39.86-27.98,64.58c0,9.61,1.52,19.04,4.53,28.04C132.58,181.44,135,186.67,138.32,192.47L138.32,192.47z"/>\
                    </g>\
                    <g>\
                        <path class="st2" d="M259.52,148.44c0,24.63-19.97,44.59-44.59,44.59s-44.59-19.97-44.59-44.59s19.97-44.59,44.59-44.59\
                            S259.52,123.82,259.52,148.44z"/>\
                        <path class="st1" d="M214.93,199.27c-28.02,0-50.83-22.8-50.83-50.83s22.8-50.82,50.83-50.82s50.83,22.8,50.83,50.82\
                            S242.96,199.27,214.93,199.27z M214.93,110.08c-21.15,0-38.36,17.21-38.36,38.36s17.21,38.36,38.36,38.36s38.36-17.21,38.36-38.36\
                            S236.08,110.08,214.93,110.08z"/>\
                    </g>\
                </g>\
            </svg>`
        );

        return (
            <GoogleMap
                mapContainerStyle={mapStyles}
                zoom={5}
                onLoad={onMapLoad}
                >
                <Marker
                    position={location.geometry.location}
                    onLoad={onMarkerLoad}
                    title={invitation.event.location}
                    icon={{
                        url: `data:image/svg+xml;utf-8, ${locIcon}`,
                        size: new google.maps.Size(56, 56),
                        scaledSize: new google.maps.Size(56, 56)
                    }}
                />
                <InfoWindow
                    position={location.geometry.location}
                    onLoad={onInfoWindowLoad}
                    >
                    <div>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom align="center">
                            {invitation.event.location}
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom align="center">
                            {invitation.event.formatted_address}
                        </Typography>
                        <Button
                            href={`https://www.google.com/maps/dir/?api=1&destination=${location.geometry.location.lat()},${location.geometry.location.lng()}`}
                            color="secondary"
                            target="_blank"
                            fullWidth
                            >
                            Get Directions
                        </Button>
                    </div>
                </InfoWindow>
            </GoogleMap>
        )
    }

    useEffect(() => {
        if (map && marker && infoWindow) {
            marker.addListener("click", () => {
                infoWindow.open({
                    anchor: marker,
                    map,
                    shouldFocus: true,
                });
            });

            infoWindow.open({
                anchor: marker,
                map,
                shouldFocus: false,
            });
        }
    }, [map, marker, infoWindow])

    // HELPER Functions ---------------------------------------------------------------------

    const showTickets = () => {
        if (! invitation?.ticket &&
            invitation.available_tickets &&
            invitation.available_tickets.length > 0
        ) return true;
        return false;
    }

    const showTicket = () => {
        if (invitation.ticket && invitation.invoice) return true;
        return false;
    }

    const showRSVP = () => {
        if (!invitation.event.rsvp) return false;
        if (invitation.invitable_type === 'App\\Models\\User' && invitation.event.guest_tickets_required) return false;
        if (invitation.invitable_type === 'App\\Models\\Group' && invitation.event.group_tickets_required) return false;
        return true;
    }

    const showInvitations = () => {
        if (invitation.event.invitations_active && invitation.invitable_type === 'App\\Models\\Group') {
            if (invitation.event.group_tickets_required && !invitation.ticket) return false;
            return true;
        }
        return false;
    }

    const datesLeft = (invitation ? (invitation.dates_allowed - invitation.dates.length) : 0);

    const showDates = i => {
        if (i.ticket && i.ticket.dates_allowed > 0) return true;
        if (!i.ticket && invitation.event.dates_allowed > 0) {
            if (!invitation.event.group_tickets_required && i.invitable_type === 'App\\Models\\Group') return true;
            if (!invitation.event.guest_tickets_required && i.invitable_type === 'App\\Models\\user') return true;
        }
        return false;
    }

    const invitesLeft = (invitation ? (invitation.event.guests_allowed - invitation.guest.invitations_sent.length) : 0);

    const getInvitationsText = () => {
        if (invitation.guest.invitations_sent.length > 0 && invitation.event.guests_allowed === 0) return `You've invited ${invitation.guest.invitations_sent.length} ${invitation.guest.invitations_sent.length === 1 ? 'guest' : 'guests'}.`
        else if (invitation.event.guests_allowed === 0) return "Click the button below to invite a guest."
        else if (invitesLeft < 1) return "You have invited your maximum number of guests."
        return `You have ${invitesLeft} invitations left to send.`
    }

    const getStatusChip = s => {
        switch (s) {
            case 'pending': return (<Chip label="Pending"/>);
            case 'accepted': return (<Chip label="Accepted" className={classes.chipSuccess}/>);
            case 'maybe': return (<Chip label="Maybe" className={classes.chipWarning}/>);
            case 'withdrawn': return (<Chip label="Withdrawn"/>);
            case 'declined': return (<Chip label="Declined" className={classes.chipError}/>);
            case 'pay_later': return (<Chip label="Payment Pending"  className={classes.chipWarning}/>);
        }
    }

    const getPaymentStatus = i => {
        const processingInvoice = getProcessingInvoice(i);

        if (i.invoice?.paid) return 'Purchased';
        else if (processingInvoice) return 'Processing';
        else return 'Purchase Ticket';
    }

    const showActionsBanner = useMemo(() => {
        if (! invitation) return false;
        if (showTickets()) return true;
        if (showTicket()) return true;
        if (showRSVP()) return true;
        if (showInvitations()) return true;
        return false;
    }, [invitation]);

    return loading ? (
        <PageLoading />
    ) : invitation ? (
        <div className={classes.container}>
            <div className={isPhone ? classes.imageContainerPhone : isMobile ? classes.imageContainerMobile : classes.imageContainerFull}>
                <div
                    className={classes.imageDiv}
                    style={{ backgroundImage: (invitation.event.image) ? `url(${invitation.event.image.full_url})` : `url(/images/default-event-background.jpg)`}}
                />
                <div className={classes.overlay} />
                <div className={classes.textDiv}>
                    <div className={classes.textItem}>
                        <Typography variant={isPhone ? "h4" : isMobile ? "h3" : "h2"} >
                            <strong>{invitation.event.name}</strong>
                        </Typography>
                        <Typography variant={isMobile ? "body1" : "h6"} gutterBottom>
                            {invitation.event.short_description}
                        </Typography>
                    </div>
                    <div className={classes.textItem}>
                        <Typography variant={isMobile ? "h6" : "h5"} >
                            <strong>{DateTime.fromISO(invitation.event.starts_at).toLocaleString(DateTime.DATETIME_MED)}</strong>
                        </Typography>
                        <Typography variant={isMobile ? "body1" : "h6"} >
                            Join us at {invitation.event.location}!
                        </Typography>
                    </div>
                </div>
            </div>
            {/*
            <div className={classes.actionsBanner}>
                <Grid 
                    container 
                    justifyContent='space-around' 
                    alignItems='center' 
                    spacing={3}
                    direction={isMobile ? 'column' : 'row'}
                    >
                    <Grid item sm="12" md="4" className={classes.textCenter}>
                        <Typography variant="h6">Your Unique QR Code:</Typography>
                    </Grid>
                    <Grid item sm="12" md="4" className={classes.textCenter}>
                        <img src={'data:image/png;base64, ' + invitation.qr_code_base} className={classes.code}/>
                    </Grid>
                    <Grid item sm="12" md="4" className={classes.textCenter}>
                        <Typography variant="subtitle2">Guest Name:</Typography>
                        <Typography variant="h6">{invitation.guest.full_name}</Typography>
                    </Grid>
                </Grid>
            </div>
            */}
            {/* showActionsBanner &&
                <div className={classes.actionsBanner}>
                    <Loading isLoading={bannerLoading} size={50} thickness={2.4} opacity={0.4}/>
                    <Grid
                        container
                        alignItems="center"
                        justify="center"
                        spacing={3}
                        >
                        {(showTickets()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>TICKETS</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {(invitation.invitable_type === 'App\\Models\\User' && invitation.event.guest_tickets_required) ? (
                                        <strong>You are required to purchase a Ticket for this Event.</strong>
                                    ) : (invitation.invitable_type === 'App\\Models\\Group' && invitation.event.group_tickets_required) ? (
                                        <strong>You are required to purchase a Ticket for this Event.</strong>
                                    ) : (
                                        <strong>Tickets are NOT required for this Event.</strong>
                                    )}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.outlinedButton}
                                    >
                                    Purchase a Ticket
                                </Button>
                            </Grid>
                        }
                        {(showTicket()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>TICKET</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {(invitation.invitable_type === 'App\\Models\\User' && !invitation.event.guest_tickets_required) ? (
                                        <strong>A ticket is NOT required for this Event.</strong>
                                    ) : (invitation.invitable_type === 'App\\Models\\Group' && invitation.event.group_tickets_required) ? (
                                        <strong>A ticket is NOT required for this Event.</strong>
                                    ) : (
                                        <strong>The following ticket is ready for purchase.</strong>
                                    )}
                                </Typography>
                                <Paper className={classes.marginBottom} elevation={4}>
                                    <ListItem ContainerComponent="div">
                                        <ListItemAvatar>
                                            <Avatar className={classes.clubAvatar}>
                                                <TicketIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <Typography
                                                        component="span"
                                                        variant="subtitle2"
                                                        color="textPrimary"
                                                        >
                                                        {invitation.ticket.title}&nbsp;-&nbsp;
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        variant="subtitle2"
                                                        color="textSecondary"
                                                        >
                                                        {invitation.ticket.description}
                                                    </Typography>
                                                </>
                                            }
                                            secondary={
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    color="textSecondary"
                                                    >
                                                    Price: <strong>${invitation.ticket.price}</strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </Paper>
                                <Button
                                    onClick={() => openPurchaseTicketDialog(invitation)}
                                    variant="outlined"
                                    className={classes.outlinedButton}
                                    >
                                    Buy Ticket
                                </Button>
                            </Grid>
                        }
                        {(showRSVP()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>RSVP</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    Let us know if you think you can make it.
                                </Typography>
                                <ButtonGroup variant="outlined" className={classes.buttonGroup}>
                                    <Button variant={(invitation.status === 'accepted') ? 'contained' : null} onClick={postAccept} >Accept</Button>
                                    <Button variant={(invitation.status === 'maybe') ? 'contained' : null} onClick={postMaybe}>Maybe</Button>
                                    <Button variant={(invitation.status === 'declined') ? 'contained' : null} onClick={postDecline}>Decline</Button>
                                </ButtonGroup>
                                {(showDates(invitation) && invitation.status === 'accepted') &&
                                    <Typography component="div" align="center" className={classes.marginTop}>
                                        <Tooltip title={(datesLeft < 1) ? "You have invited the maximum number of dates allowed." : "Add a Date"}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => openEditDateDialog(null)}
                                                    disabled={(datesLeft < 1)}
                                                    className={classes.outlinedButton}
                                                    >
                                                    Add a date to your invitation
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Typography>
                                }
                            </Grid>
                        }
                        {(showInvitations()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>INVITATIONS</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {getInvitationsText()}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.outlinedButton}
                                    onClick={openInviteGuestDialog}
                                    disabled={(invitation.event.guests_allowed > 0 && invitesLeft < 1)}
                                    >
                                    {(invitation.guest.invitations_sent.length > 0) ? 'Invite Another Guest' : 'Invite Guest'}
                                </Button>
                            </Grid>
                        }
                    </Grid>
                </div>
                    */}
            <div className={classes.description}>
                <Typography
                    variant="body1"
                    component="p"
                    color="textSecondary"
                    dangerouslySetInnerHTML={{__html: invitation.event.description}}
                />
            </div>
            {showActionsBanner &&
                <div className={classes.actionsBanner}>
                    <Loading isLoading={bannerLoading} size={50} thickness={2.4} opacity={0.4}/>
                    <Grid
                        container
                        alignItems="center"
                        justify="center"
                        spacing={3}
                        >
                        {(showTickets()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>TICKETS</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {(invitation.invitable_type === 'App\\Models\\User' && invitation.event.guest_tickets_required) ? (
                                        <strong>You are required to purchase a Ticket for this Event.</strong>
                                    ) : (invitation.invitable_type === 'App\\Models\\Group' && invitation.event.group_tickets_required) ? (
                                        <strong>You are required to purchase a Ticket for this Event.</strong>
                                    ) : (
                                        <strong>Tickets are NOT required for this Event.</strong>
                                    )}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.outlinedButton}
                                    onClick={() => openSelectTicketDialog(invitation)}
                                    >
                                    Select a Ticket
                                </Button>
                            </Grid>
                        }
                        {(showTicket()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>TICKET</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {(invitation.invoice && invitation.invoice.paid) ? (
                                        <strong>You have purchased the following ticket:</strong>
                                    ) : (invitation.invoice && !invitation.invoice.paid) ? (
                                        <strong>The following ticket is ready for purchase:</strong>
                                    ) : (
                                        <strong>You have reserved the following ticket:</strong>
                                    )}
                                </Typography>
                                <Paper className={classes.marginBottom} elevation={4}>
                                    <ListItem ContainerComponent="div">
                                        <ListItemAvatar>
                                            <Avatar className={classes.clubAvatar}>
                                                <TicketIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <Typography
                                                        component="span"
                                                        variant="subtitle2"
                                                        color="textPrimary"
                                                        >
                                                        {invitation.ticket.title}&nbsp;-&nbsp;
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        variant="subtitle2"
                                                        color="textSecondary"
                                                        >
                                                        {invitation.ticket.description}
                                                    </Typography>
                                                </>
                                            }
                                            secondary={
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    color="textSecondary"
                                                    >
                                                    Price: <strong>${invitation.ticket.price}</strong>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </Paper>
                                {(invitation.invoice && !invitation.invoice.paid) &&
                                    <Button
                                        onClick={() => openPurchaseTicketDialog(invitation)}
                                        variant="outlined"
                                        className={classes.outlinedButton}
                                        >
                                        Buy Ticket
                                    </Button>
                                }
                            </Grid>
                        }
                        {(showRSVP()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>RSVP</Typography>
                                {invitation.invitable_type === 'App\\Models\\user' ? (
                                    <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                        <strong>{invitation.invitable.first} {invitation.invitable.last}</strong> has invited you.<br/>
                                        Let them know if you think you can make it.
                                    </Typography>
                                ) : (
                                    <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                        Let us know if you think you can make it.
                                    </Typography>
                                )}
                                
                                <ButtonGroup variant="outlined" className={classes.buttonGroup}>
                                    <Button variant={(invitation.status === 'accepted') ? 'contained' : null} onClick={postAccept} >Accept</Button>
                                    <Button variant={(invitation.status === 'maybe') ? 'contained' : null} onClick={postMaybe}>Maybe</Button>
                                    <Button variant={(invitation.status === 'declined') ? 'contained' : null} onClick={postDecline}>Decline</Button>
                                </ButtonGroup>
                                {(showDates(invitation) && invitation.status === 'accepted') &&
                                    <Typography component="div" align="center" className={classes.marginTop}>
                                        <Tooltip title={(datesLeft < 1) ? "You have invited the maximum number of dates allowed." : "Add a Date"}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => openEditDateDialog(null)}
                                                    disabled={(datesLeft < 1)}
                                                    className={classes.outlinedButton}
                                                    >
                                                    Add a date to your invitation
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Typography>
                                }
                            </Grid>
                        }
                        {(showInvitations()) &&
                            <Grid item xs={12} md={4} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>INVITATIONS</Typography>
                                <Typography variant="subtitle2" align="center" className={classes.marginBottom}>
                                    {getInvitationsText()}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.outlinedButton}
                                    onClick={openInviteGuestDialog}
                                    disabled={(invitation.event.guests_allowed > 0 && invitesLeft < 1)}
                                    >
                                    {(invitation.guest.invitations_sent.length > 0) ? 'Invite Another Guest' : 'Invite Guest'}
                                </Button>
                            </Grid>
                        }
                    </Grid>
                </div>
            }
            {(showDates(invitation) || showInvitations()) &&
                <div className={classes.invitationContainer}>
                    <Grid
                        container
                        spacing={3}
                        justify="center"
                        >
                        {(showDates(invitation)) &&
                            <Grid item xs={12} md={6}>
                                <Grid
                                    container
                                    justify="space-between"
                                    alignItems="center"
                                    className={classes.marginBottom}
                                    >
                                    <Grid item>
                                        <Typography
                                            variant="h4"
                                            color="secondary"
                                            >
                                            Dates
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }}/>
                                        <TextField
                                            id="dates-search"
                                            label="Search Dates"
                                            type="search"
                                            size="small"
                                            variant="filled"
                                            value={dateSearchValue}
                                            fullWidth
                                            margin="dense"
                                            onChange={handleDateSearchValue}
                                            InputProps={{ className: classes.bottomRadius, disableUnderline: true }}
                                            color="secondary"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title={(datesLeft < 1) ? "You have invited the maximum number of dates allowed." : "Add a Date"}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => openEditDateDialog(null)}
                                                    disabled={(datesLeft < 1)}
                                                    color="secondary"
                                                    >
                                                    Add Date
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Typography color="textSecondary" className={classes.marginBottom}>
                                    Here you can view, add and edit the dates you have added to your invitation. Adding dates will help event coordinators with invitations.
                                </Typography>
                                <div className={classes.userContainer}>
                                    {filteredDates.length > 0 ? filteredDates.slice(((datesPage-1) * 5), (((datesPage - 1) * 5) + 5)).map(d => (
                                        <Card key={d.id} className={classes.marginBottom}>
                                            <CardHeader
                                                avatar={
                                                    <Avatar arai-label="guest" className={classes.clubAvatar} >
                                                        <UserIcon />
                                                    </Avatar>
                                                }
                                                title={d.name}
                                                subheader={`Added ${DateTime.fromISO(d.created_at).toLocaleString(DateTime.DATE_MED)}`}
                                                classes={{ action: classes.cardAction }}
                                                action={
                                                    <Button
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => openEditDateDialog(d)}
                                                        >
                                                        Edit
                                                    </Button>
                                                }
                                            />
                                        </Card>
                                    )) : (
                                        <Paper>
                                            <Typography
                                                align="center"
                                                color="textSecondary"
                                                variant="subtitle2"
                                                className={classes.empty}
                                                >
                                                You have not yet invited any dates. Click <strong>ADD DATE</strong> to add a date to your invitation.
                                            </Typography>
                                        </Paper>
                                    )}
                                </div>
                                {(filteredDates.length > 5) &&
                                    <Pagination
                                        count={Math.ceil((filteredDates.length) / 5)}
                                        page={datesPage}
                                        onChange={handleChangeDatesPage}
                                        className={classes.pagination}
                                    />
                                }
                            </Grid>
                        }
                        {(showInvitations()) &&
                            <Grid item xs={12} md={6}>
                                <Grid
                                    container
                                    justify="space-between"
                                    alignItems="center"
                                    className={classes.marginBottom}
                                    >
                                    <Grid item>
                                        <Typography
                                            variant="h4"
                                            color="secondary"
                                            >
                                            Guests
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }}/>
                                        <TextField
                                            id="guests-search"
                                            label="Search Guests"
                                            type="search"
                                            size="small"
                                            variant="filled"
                                            value={guestSearchValue}
                                            fullWidth
                                            onChange={handleGuestSearchValue}
                                            margin="dense"
                                            InputProps={{ className: classes.bottomRadius, disableUnderline: true }}
                                            color="secondary"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title={(invitation.event.guests_allowed > 0 && invitesLeft < 1) ? "You have invited the maximum number of guests allowed." : "Invite a Guest"}>
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={openInviteGuestDialog}
                                                    disabled={(invitation.event.guests_allowed > 0 && invitesLeft < 1)}
                                                    color="secondary"
                                                    >
                                                    Invite Guest
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Typography color="textSecondary" className={classes.marginBottom}>
                                    Here you can view the guests you have invited, edit their invitations and pay for their tickets if possible.
                                </Typography>
                                <div className={classes.userContainer}>
                                    {filteredGuests.length > 0 ? filteredGuests.slice(((guestsPage-1) * 5), (((guestsPage - 1) * 5) + 5)).map( i => (
                                        <Card key={i.id} className={classes.marginBottom}>
                                            <CardHeader
                                                avatar={
                                                    <Avatar arai-label="guest" className={classes.clubAvatar} >
                                                        {i.guest.initials}
                                                    </Avatar>
                                                }
                                                title={(i.guest.prefix + " " + i.guest.first + " " + i.guest.last).trim()}
                                                subheader={`Invited ${DateTime.fromISO(i.created_at).toLocaleString(DateTime.DATE_MED)}`}
                                                action={getStatusChip(i.status)}
                                                classes={{ action: classes.cardAction }}
                                            />
                                            {i?.ticket &&
                                                <CardContent className={classes.noPadding}>
                                                    <Chip
                                                        avatar={<TicketIcon/>}
                                                        label={`Ticket: ${i.ticket.title} ${(i.variant && i.variant != "") ? "(" + i.variant + ")" : ""}`}
                                                        color="secondary"
                                                    />
                                                </CardContent>
                                            }
                                            <CardActions className={classes.card}>
                                                <div>
                                                    {showDates(i) &&
                                                        <Button
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => openEditDatesDialog(i)}
                                                            >
                                                            {i.dates.length} Dates Invited
                                                        </Button>
                                                    }
                                                </div>
                                                <div>
                                                    <Tooltip title={"Edit Invitation"}>
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                color="secondary"
                                                                onClick={() => openEditGuestDialog(i)}
                                                                >
                                                                Edit
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    {i.invoice &&
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() => openPurchaseTicketDialog(i)}
                                                            disabled={getPaymentStatus(i) !== 'Purchase Ticket'}
                                                            >
                                                            {getPaymentStatus(i)}
                                                        </Button>
                                                    }
                                                </div>
                                            </CardActions>
                                        </Card>
                                    )) : (
                                        <Paper className={classes.marginBottom}>
                                            <Typography
                                                align="center"
                                                color="textSecondary"
                                                variant="subtitle2"
                                                className={classes.empty}
                                                >
                                                You have not yet invited any guests. Click <strong>ADD GUEST</strong> to add a guest to your invitation.
                                            </Typography>
                                        </Paper>
                                    )}
                                </div>
                                {(filteredGuests.length > 5) &&
                                    <Pagination
                                        count={Math.ceil((filteredGuests.length) / 5)}
                                        page={guestsPage}
                                        onChange={handleChangeGuestsPage}
                                        className={classes.pagination}
                                    />
                                }
                            </Grid>
                        }
                    </Grid>
                </div>
            }
            {(location) &&
                <div className={classes.mapContainer}>
                    {renderMap()}
                </div>
            }
            <div className={classes.actionsBanner}>
                {(location) &&
                    <Typography
                        align="center"
                        className={classes.marginBottom}
                        >
                        <Button
                            href={`https://www.google.com/maps/dir/?api=1&destination=${location.geometry.location.lat()},${location.geometry.location.lng()}`}
                            target="_blank"
                            variant="outlined"
                            className={classes.outlinedButton}
                            >
                            Get Directions
                        </Button>
                    </Typography>
                }
                <Typography
                    align="center"
                    color="inherit"
                    variant="subtitle2"
                    gutterBottom
                    >
                    We look forward to you joining us for {invitation.event.name}.
                </Typography>
                <Typography
                    align="center"
                    color="inherit"
                    variant="subtitle1"
                    >
                    {app.club.name}
                </Typography>
            </div>
            <InviteGuestDialog
                invitation={invitation}
                open={inviteGuestDialogOpen}
                onClose={closeInviteGuestDialog}
                onSave={closeInviteGuestDialogAndRefresh}
            />
            <SelectTicketDialog
                invitation={invitation}
                open={selectTicketDialogOpen}
                onClose={closeSelectTicketDialog}
                onSave={closeSelectTicketDialogAndRefresh}
            />
            <EditInvitationDialog
                userInvitation={invitation}
                guestInvitation={activeInvitation}
                open={editGuestDialogOpen}
                onClose={closeEditGuestDialog}
                onSave={closeEditGuestDialogAndRefresh}
                onClosed={editGuestDialogClosed}
            />
            <EditDateDialog
                invitation={invitation}
                initDate={activeDate}
                open={editDateDialogOpen}
                onClose={closeEditDateDialog}
                onSave={closeEditDateDialogAndRefresh}
                onClosed={editDateDialogClosed}
            />
            <EditDatesDialog
                invitation={activeInvitation}
                open={editDatesDialogOpen}
                onClose={closeEditDatesDialog}
                onSave={closeEditDatesDialogAndRefresh}
                onClosed={editDatesDialogClosed}
            />
            <PurchaseTicketDialog
                user={app.user}
                club={app.club}
                invitation={activeInvoiceInvitation}
                urlPrefix={'/profile'}
                open={purchaseTicketDialogOpen}
                onClose={closePurchaseTicketDialog}
                onClosed={purchaseTicketDialogClosed}
                onCharge={closePurchaseTicketDialogAndRefresh}
            />
            {/*}
            <Dialog
                open={guestDialogOpen}
                onClose={closeGuestDialog}
                unMountOnExit
                >
                <DialogTitle>
                    <span>Invitation for {activeGuest.guest.full_name}</span>
                    <IconButton aria-label="Cancel" className={classes.closeButton} onClick={closeGuestDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                        <Typography gutterBottom>
                            <strong>Message: </strong> {activeGuest.message}
                        </Typography>
                        <Typography className={classes.marginTop}>
                            <strong>Ticket: </strong> {activeGuest.ticket.title} - ${activeGuest.ticket.price} ({activeGuest.ticket.paid ? 'paid' : 'unpaid'})
                        </Typography>
                </DialogContent>
                <DialogActions className={classes.marginTop}>
                    <Button variant='outlined' color='primary' onClick={()=>postResend(activeGuest.id)}>
                        Resend Invitation
                    </Button>
                    <RedButton onClick={()=>postWithdraw(activeGuest.id)}>
                        Withdraw Invitation
                    </RedButton>
                </DialogActions>
            </Dialog>
                */}
        </div>
    ) : (
        <div className={classes.container}>
            Could not load event.
        </div>
    )
}
